/**
 * Pre-written message templates for campaigns and reminders (task #603).
 *
 * Hosts open the message box and face an empty textarea. The shapes that work
 * are well known — a formal invitation names the couple and the date and then
 * gets out of the way; a nudge to non-responders says why it matters; a day-of
 * message gives times and nothing else — so this offers them as starting points
 * rather than making everyone invent one.
 *
 * Design notes:
 *
 *  • Templates are a STARTING POINT, not a send. Anything the host must supply
 *    is written in [square brackets], and the picker refuses nothing — but the
 *    message box warns while any bracket is still unfilled, because "Dear
 *    [couple's names]" reaching four hundred guests is the one failure this
 *    feature could plausibly cause.
 *
 *  • EVERY template names the host (round 2). A campaign arrives from the
 *    QuikRSVP number, so a guest has no idea who is writing unless the message
 *    says — an invitation nobody can attribute is one nobody answers. Where the
 *    wording already names them ("the wedding of [names]") that is the naming;
 *    everywhere else there is a sign-off.
 *
 *  • Only the personalization tokens that actually resolve are used —
 *    {{name}} and {{first_name}}. {{table_number}} is deliberately absent:
 *    it needs a seating chart, and a template that quietly produces a blank on
 *    events without one would be worse than no template.
 *
 *  • Written in the language being worked in (round 2). English is the source;
 *    TEMPLATE_I18N carries the other six, keyed by template id, and anything
 *    missing falls back to English rather than disappearing. The caller passes
 *    the language it is composing in; failing that we read the page's.
 */
window.MessageTemplates = (function () {
  'use strict';

  // The event types the builder and the invitation designer use, grouped into
  // the four families whose messages genuinely differ. Anything unrecognised
  // falls through to `general`.
  const FAMILY_BY_EVENT_TYPE = {
    wedding: 'wedding', engagement: 'wedding', anniversary: 'wedding',
    bar_mitzvah: 'milestone', bat_mitzvah: 'milestone', bris: 'milestone',
    christening: 'milestone', first_communion: 'milestone', confirmation: 'milestone',
    graduation: 'milestone', sweet_sixteen: 'milestone',
    birthday: 'celebration', party: 'celebration', babyshower: 'celebration',
    gender_reveal: 'celebration', housewarming: 'celebration',
    retirement: 'celebration', promotion: 'celebration',
    corporate: 'corporate', fundraiser: 'corporate'
  };

  const INVITATION = {
    wedding: [
      {
        id: 'wedding-formal',
        name: 'Formal invitation',
        hint: 'The classic shape — who, when, and where to reply.',
        body: 'Dear {{name}},\n\n'
          + 'You are invited to the wedding of [names] on [date] at [place].\n\n'
          + 'To RSVP and see all the details, please tap the link below.\n\n'
          + 'With love,\n[names]'
      },
      {
        id: 'wedding-warm',
        name: 'Warm and short',
        hint: 'For a guest list of friends rather than a formal one.',
        body: "Hi {{first_name}}, it's [names] — we're getting married!\n\n"
          + "We'd love you to be there. Timings, directions and RSVP are all on the link below."
      },
      {
        id: 'wedding-save-the-date',
        name: 'Save the date',
        hint: 'Sent early, before the full invitation.',
        body: 'Hi {{first_name}}, save the date — [names] are getting married on [date].\n\n'
          + 'The full invitation follows, but do let us know now if you think you can make it.\n\n'
          + '[names]'
      }
    ],
    milestone: [
      {
        id: 'milestone-formal',
        name: 'Formal invitation',
        hint: 'Names the occasion and the person it is for.',
        body: 'Dear {{name}},\n\n'
          + "You are invited to celebrate [name]'s [occasion] on [date] at [place].\n\n"
          + 'To RSVP and see all the details, please tap the link below.\n\n'
          + '[host names]'
      },
      {
        id: 'milestone-warm',
        name: 'Warm and short',
        hint: 'Friendly, and quick to read on a phone.',
        body: "Hi {{first_name}}, it's [host names] — we're celebrating [name]'s [occasion] on [date] and we'd love you there.\n\n"
          + 'Everything you need is on the link below.'
      }
    ],
    celebration: [
      {
        id: 'celebration-invite',
        name: 'Party invitation',
        hint: 'The details up front, RSVP on the link.',
        body: "Hi {{first_name}}, you're invited!\n\n"
          + '[occasion] on [date] at [place], from [time].\n\n'
          + 'Let us know if you can make it on the link below.\n\n'
          + '[host names]'
      },
      {
        id: 'celebration-short',
        name: 'Short and friendly',
        hint: 'Two lines. Best when everyone already knows what it is.',
        body: 'Hi {{first_name}} — [occasion], [date], [place]. Come!\n\n'
          + 'RSVP on the link below.\n\n'
          + '[host names]'
      }
    ],
    corporate: [
      {
        id: 'corporate-formal',
        name: 'Formal invitation',
        hint: 'Neutral and precise, for a professional list.',
        body: 'Dear {{name}},\n\n'
          + 'We are pleased to invite you to [event] on [date] at [venue].\n\n'
          + 'Please confirm your attendance using the link below.\n\n'
          + '[your name]\n[organisation]'
      },
      {
        id: 'corporate-brief',
        name: 'Short and direct',
        hint: 'For a list that already knows the event.',
        body: 'Hello {{first_name}},\n\n'
          + '[event] takes place on [date] at [venue]. Please register using the link below — places are limited.\n\n'
          + '[organisation]'
      }
    ],
    general: [
      {
        id: 'general-invite',
        name: 'Simple invitation',
        hint: 'Works for anything.',
        body: 'Hi {{name}},\n\n'
          + "You're invited to [event] on [date] at [place].\n\n"
          + 'Please let us know if you can come using the link below.\n\n'
          + '[host names]'
      },
      {
        id: 'general-short',
        name: 'Short and friendly',
        hint: 'One line and a link.',
        body: "Hi {{first_name}} — it's [host names]. You're invited to [event] on [date]. RSVP on the link below."
      }
    ]
  };

  // Reminders are keyed by AUDIENCE, because that is the choice the host has
  // just made in the composer above the message box — a nudge to someone who
  // hasn't answered and a day-of note to someone who is coming have nothing in
  // common. 'all' carries the messages that suit any audience.
  const REMINDER = {
    no_response: [
      {
        id: 'rem-nudge',
        name: 'Gentle nudge',
        hint: 'The first reminder. Light, no pressure.',
        body: "Hi {{first_name}}, it's [host names] — a quick reminder about [event]. We haven't heard from you yet and we'd love to know whether you can join us.\n\n"
          + 'It only takes a moment on the link below.'
      },
      {
        id: 'rem-deadline',
        name: 'Numbers close soon',
        hint: 'The one that actually gets replies — it says why now.',
        body: "Hi {{first_name}}, we're finalising numbers for [event] on [date].\n\n"
          + "If you'd like to join us, please let us know before then using the link below.\n\n"
          + '[host names]'
      },
      {
        id: 'rem-last-call',
        name: 'Last call',
        hint: 'The final one. Short, and clear that it is the last.',
        body: 'Hi {{first_name}}, last call — we close the guest list for [event] [today/tomorrow].\n\n'
          + "If we don't hear from you we'll assume you can't make it. The link is below if you can.\n\n"
          + '[host names]'
      }
    ],
    attending: [
      {
        id: 'rem-day-of',
        name: 'The day itself',
        hint: 'Times and nothing else. Sent the morning of.',
        body: 'The big day is finally here!\n\n'
          + "We're so excited to celebrate with you.\n\n"
          + 'Ceremony at [time].\n'
          + 'Reception at [time].\n\n'
          + '[host names]'
      },
      {
        id: 'rem-practical',
        name: 'Practical details',
        hint: 'Sent a day or two before, for anything they need to know.',
        body: "Hi {{first_name}}, it's [host names] — see you on [date]!\n\n"
          + 'Doors open at [time] at [place]. [parking / dress code / anything else they need].'
      }
    ],
    maybe: [
      {
        id: 'rem-maybe',
        name: 'Any news?',
        hint: 'Warm, and explains why the answer matters.',
        body: "Hi {{first_name}}, it's [host names] — you said maybe to [event]. Any news?\n\n"
          + "We'd love to have you, and knowing either way really helps us plan. The link below is still open."
      }
    ],
    declined: [
      {
        id: 'rem-declined-warm',
        name: 'Sorry to miss you',
        hint: 'Sent on the day, to the people who cannot come.',
        body: "We're so sorry you can't join us in person, but we hope you'll celebrate with us from afar.\n\n"
          + '[live stream link / how they can be part of it]\n\n'
          + '[host names]'
      }
    ],
    all: [
      {
        id: 'rem-thank-you',
        name: 'Thank you, afterwards',
        hint: 'Sent the day after. Costs nothing and is always remembered.',
        body: 'Thank you so much to everyone who celebrated with us, in person and from afar.\n\n'
          + 'It meant the world to us, and we look forward to celebrating together again.\n\n'
          + '[host names]'
      },
      {
        id: 'rem-change',
        name: 'Something has changed',
        hint: 'For a change of time, place or plan.',
        body: "Hi {{first_name}}, it's [host names] — one update about [event]:\n\n"
          + '[what changed]\n\n'
          + 'Everything else stays the same. Sorry for the short notice!'
      }
    ]
  };

  // English is the source above; everything else lives here, keyed by template
  // id. A language that is missing a template falls back to the English one —
  // a gap must show up as untranslated wording, never as a missing template.
  const EN_UI = {
    title: 'Message templates',
    reminderTitle: 'Reminder templates',
    lede: 'Pick a starting point and edit it — the highlighted bits are yours to fill in.',
    use: 'Use this',
    replace: 'Replace what you have written with this template?',
    blanksLead: 'Still to fill in:',
    andMore: 'and {n} more',
    allSections: 'All',
    // The fill-in step (round 3). Picking a template used to drop raw
    // "[names] ... [date]" into the box and leave the host to hunt for every
    // bracket; it now asks once and substitutes everywhere.
    fillTitle: "Fill in the details",
    fillLede: "Fill in the details below to personalize this message.",
    insert: "Insert message",
    back: "Back to templates",
    previewLabel: "Preview",
  };
  const EN_FAMILIES = {
    wedding: 'Wedding', milestone: 'Milestone', celebration: 'Celebration',
    corporate: 'Corporate', general: 'Any event'
  };
  const EN_AUDIENCES = {
    no_response: "Guests who haven't answered",
    attending: 'Guests who are coming',
    maybe: 'Guests who said maybe',
    declined: "Guests who can't come",
    all: 'Any audience'
  };

  const TEMPLATE_I18N = {
    es: {
      ui: {
        title: 'Plantillas de mensaje',
        reminderTitle: 'Plantillas de recordatorio',
        lede: 'Elige un punto de partida y edítalo: lo resaltado es lo que tienes que completar.',
        use: 'Usar esta',
        replace: '¿Reemplazar lo que has escrito por esta plantilla?',
        blanksLead: 'Falta completar:',
        andMore: 'y {n} más',
        allSections: 'Todas',
        fillTitle: "Completa los datos",
        fillLede: "Completa los datos de abajo para personalizar este mensaje.",
        insert: "Insertar mensaje",
        back: "Volver a las plantillas",
        previewLabel: "Vista previa",
      },
      families: { wedding: 'Boda', milestone: 'Celebración religiosa', celebration: 'Fiesta', corporate: 'Corporativo', general: 'Cualquier evento' },
      audiences: {
        no_response: 'Invitados que no han respondido',
        attending: 'Invitados que asisten',
        maybe: 'Invitados que dijeron quizás',
        declined: 'Invitados que no pueden ir',
        all: 'Cualquier público'
      },
      t: {
        'wedding-formal': {
          name: 'Invitación formal', hint: 'La forma clásica: quién, cuándo y dónde responder.',
          body: 'Estimado/a {{name}}:\n\nTe invitamos a la boda de [nombres] el [fecha] en [lugar].\n\nPara confirmar tu asistencia y ver todos los detalles, pulsa el enlace de abajo.\n\nCon cariño,\n[nombres]'
        },
        'wedding-warm': {
          name: 'Cercana y breve', hint: 'Para una lista de amigos, no una formal.',
          body: 'Hola {{first_name}}, somos [nombres]: ¡nos casamos!\n\nNos encantaría que vinieras. Horarios, cómo llegar y confirmación, todo en el enlace de abajo.'
        },
        'wedding-save-the-date': {
          name: 'Reserva la fecha', hint: 'Se envía pronto, antes de la invitación completa.',
          body: 'Hola {{first_name}}, reserva la fecha: [nombres] se casan el [fecha].\n\nLa invitación completa llegará después, pero cuéntanos ya si crees que podrás venir.\n\n[nombres]'
        },
        'milestone-formal': {
          name: 'Invitación formal', hint: 'Nombra la ocasión y a quién se celebra.',
          body: 'Estimado/a {{name}}:\n\nTe invitamos a celebrar el [ocasión] de [nombre] el [fecha] en [lugar].\n\nPara confirmar y ver todos los detalles, pulsa el enlace de abajo.\n\n[nombres de los anfitriones]'
        },
        'milestone-warm': {
          name: 'Cercana y breve', hint: 'Amable y rápida de leer en el móvil.',
          body: 'Hola {{first_name}}, somos [nombres de los anfitriones]: celebramos el [ocasión] de [nombre] el [fecha] y nos encantaría verte allí.\n\nTodo lo que necesitas está en el enlace de abajo.'
        },
        'celebration-invite': {
          name: 'Invitación a la fiesta', hint: 'Los detalles primero, la confirmación en el enlace.',
          body: 'Hola {{first_name}}, ¡estás invitado/a!\n\n[ocasión] el [fecha] en [lugar], a partir de las [hora].\n\nDinos si puedes venir en el enlace de abajo.\n\n[nombres de los anfitriones]'
        },
        'celebration-short': {
          name: 'Breve y cercana', hint: 'Dos líneas. Ideal cuando ya saben de qué se trata.',
          body: 'Hola {{first_name}}: [ocasión], [fecha], [lugar]. ¡Ven!\n\nConfirma en el enlace de abajo.\n\n[nombres de los anfitriones]'
        },
        'corporate-formal': {
          name: 'Invitación formal', hint: 'Neutra y precisa, para una lista profesional.',
          body: 'Estimado/a {{name}}:\n\nNos complace invitarle a [evento] el [fecha] en [lugar].\n\nPor favor, confirme su asistencia en el enlace de abajo.\n\n[tu nombre]\n[empresa]'
        },
        'corporate-brief': {
          name: 'Breve y directa', hint: 'Para una lista que ya conoce el evento.',
          body: 'Hola {{first_name}}:\n\n[evento] se celebra el [fecha] en [lugar]. Regístrese en el enlace de abajo: las plazas son limitadas.\n\n[empresa]'
        },
        'general-invite': {
          name: 'Invitación sencilla', hint: 'Sirve para cualquier cosa.',
          body: 'Hola {{name}}:\n\nEstás invitado/a a [evento] el [fecha] en [lugar].\n\nDinos si puedes venir usando el enlace de abajo.\n\n[nombres de los anfitriones]'
        },
        'general-short': {
          name: 'Breve y cercana', hint: 'Una línea y un enlace.',
          body: 'Hola {{first_name}}, somos [nombres de los anfitriones]. Estás invitado/a a [evento] el [fecha]. Confirma en el enlace de abajo.'
        },
        'rem-nudge': {
          name: 'Recordatorio suave', hint: 'El primer recordatorio. Ligero, sin presión.',
          body: 'Hola {{first_name}}, somos [nombres de los anfitriones]: un recordatorio rápido sobre [evento]. Todavía no sabemos nada de ti y nos encantaría saber si podrás acompañarnos.\n\nSolo lleva un momento en el enlace de abajo.'
        },
        'rem-deadline': {
          name: 'Cerramos números', hint: 'El que de verdad consigue respuestas: dice por qué ahora.',
          body: 'Hola {{first_name}}, estamos cerrando el número de invitados de [evento] el [fecha].\n\nSi quieres acompañarnos, dínoslo antes usando el enlace de abajo.\n\n[nombres de los anfitriones]'
        },
        'rem-last-call': {
          name: 'Último aviso', hint: 'El último. Breve y claro en que es el último.',
          body: 'Hola {{first_name}}, último aviso: cerramos la lista de [evento] [hoy/mañana].\n\nSi no sabemos nada, entenderemos que no puedes venir. El enlace está abajo por si puedes.\n\n[nombres de los anfitriones]'
        },
        'rem-day-of': {
          name: 'El día del evento', hint: 'Horarios y nada más. Se envía esa misma mañana.',
          body: '¡Por fin llegó el gran día!\n\nTenemos muchas ganas de celebrarlo contigo.\n\nCeremonia a las [hora].\nRecepción a las [hora].\n\n[nombres de los anfitriones]'
        },
        'rem-practical': {
          name: 'Detalles prácticos', hint: 'Un día o dos antes, para lo que necesiten saber.',
          body: 'Hola {{first_name}}, somos [nombres de los anfitriones]: ¡nos vemos el [fecha]!\n\nSe abre a las [hora] en [lugar]. [aparcamiento / código de vestimenta / lo que necesiten saber].'
        },
        'rem-maybe': {
          name: '¿Alguna novedad?', hint: 'Cercano, y explica por qué importa la respuesta.',
          body: 'Hola {{first_name}}, somos [nombres de los anfitriones]: dijiste que quizás a [evento]. ¿Alguna novedad?\n\nNos encantaría contar contigo, y saberlo de un modo u otro nos ayuda mucho a organizar. El enlace de abajo sigue abierto.'
        },
        'rem-declined-warm': {
          name: 'Te echaremos de menos', hint: 'Se envía el mismo día a quienes no pueden ir.',
          body: 'Sentimos mucho que no puedas acompañarnos en persona, pero esperamos que lo celebres con nosotros en la distancia.\n\n[enlace de la retransmisión / cómo pueden participar]\n\n[nombres de los anfitriones]'
        },
        'rem-thank-you': {
          name: 'Gracias, después', hint: 'Al día siguiente. No cuesta nada y siempre se recuerda.',
          body: 'Muchísimas gracias a todos los que lo celebraron con nosotros, en persona y en la distancia.\n\nSignificó muchísimo para nosotros y esperamos volver a celebrar juntos.\n\n[nombres de los anfitriones]'
        },
        'rem-change': {
          name: 'Ha cambiado algo', hint: 'Para un cambio de hora, lugar o plan.',
          body: 'Hola {{first_name}}, somos [nombres de los anfitriones]: una novedad sobre [evento].\n\n[qué ha cambiado]\n\nTodo lo demás sigue igual. ¡Perdón por el poco margen!'
        }
      }
    },

    fr: {
      ui: {
        title: 'Modèles de message',
        reminderTitle: 'Modèles de rappel',
        lede: 'Choisissez un point de départ et modifiez-le — les parties surlignées sont à compléter.',
        use: 'Utiliser',
        replace: 'Remplacer ce que vous avez écrit par ce modèle ?',
        blanksLead: 'Reste à compléter :',
        andMore: 'et {n} de plus',
        allSections: 'Tous',
        fillTitle: "Complétez les détails",
        fillLede: "Renseignez les détails ci-dessous pour personnaliser ce message.",
        insert: "Insérer le message",
        back: "Retour aux modèles",
        previewLabel: "Aperçu",
      },
      families: { wedding: 'Mariage', milestone: 'Grande occasion', celebration: 'Fête', corporate: 'Professionnel', general: 'Tout événement' },
      audiences: {
        no_response: "Invités qui n'ont pas répondu",
        attending: 'Invités qui viennent',
        maybe: 'Invités qui ont répondu peut-être',
        declined: 'Invités qui ne peuvent pas venir',
        all: 'Tous les invités'
      },
      t: {
        'wedding-formal': {
          name: 'Invitation formelle', hint: 'La forme classique : qui, quand, et où répondre.',
          body: 'Cher/Chère {{name}},\n\nVous êtes invité(e) au mariage de [noms] le [date] à [lieu].\n\nPour répondre et voir tous les détails, appuyez sur le lien ci-dessous.\n\nAvec toute notre affection,\n[noms]'
        },
        'wedding-warm': {
          name: 'Chaleureuse et courte', hint: 'Pour une liste d’amis plutôt que formelle.',
          body: 'Bonjour {{first_name}}, c’est [noms] — nous nous marions !\n\nNous aimerions beaucoup vous compter parmi nous. Horaires, accès et réponse : tout est sur le lien ci-dessous.'
        },
        'wedding-save-the-date': {
          name: 'Réservez la date', hint: 'Envoyée tôt, avant l’invitation complète.',
          body: 'Bonjour {{first_name}}, réservez la date : [noms] se marient le [date].\n\nL’invitation complète suivra, mais dites-nous dès maintenant si vous pensez pouvoir venir.\n\n[noms]'
        },
        'milestone-formal': {
          name: 'Invitation formelle', hint: 'Nomme l’occasion et la personne fêtée.',
          body: 'Cher/Chère {{name}},\n\nVous êtes invité(e) à célébrer [occasion] de [prénom] le [date] à [lieu].\n\nPour répondre et voir tous les détails, appuyez sur le lien ci-dessous.\n\n[noms des hôtes]'
        },
        'milestone-warm': {
          name: 'Chaleureuse et courte', hint: 'Amicale, et vite lue sur un téléphone.',
          body: 'Bonjour {{first_name}}, c’est [noms des hôtes] — nous fêtons [occasion] de [prénom] le [date] et nous aimerions vous y voir.\n\nTout ce qu’il vous faut est sur le lien ci-dessous.'
        },
        'celebration-invite': {
          name: 'Invitation à la fête', hint: 'Les détails d’abord, la réponse sur le lien.',
          body: 'Bonjour {{first_name}}, vous êtes invité(e) !\n\n[occasion] le [date] à [lieu], à partir de [heure].\n\nDites-nous si vous pouvez venir via le lien ci-dessous.\n\n[noms des hôtes]'
        },
        'celebration-short': {
          name: 'Courte et amicale', hint: 'Deux lignes. Parfait quand tout le monde sait déjà.',
          body: 'Bonjour {{first_name}} — [occasion], [date], [lieu]. Venez !\n\nRépondez sur le lien ci-dessous.\n\n[noms des hôtes]'
        },
        'corporate-formal': {
          name: 'Invitation formelle', hint: 'Neutre et précise, pour une liste professionnelle.',
          body: 'Cher/Chère {{name}},\n\nNous avons le plaisir de vous inviter à [événement] le [date] à [lieu].\n\nMerci de confirmer votre présence via le lien ci-dessous.\n\n[votre nom]\n[entreprise]'
        },
        'corporate-brief': {
          name: 'Courte et directe', hint: 'Pour une liste qui connaît déjà l’événement.',
          body: 'Bonjour {{first_name}},\n\n[événement] a lieu le [date] à [lieu]. Merci de vous inscrire via le lien ci-dessous — les places sont limitées.\n\n[entreprise]'
        },
        'general-invite': {
          name: 'Invitation simple', hint: 'Convient à tout.',
          body: 'Bonjour {{name}},\n\nVous êtes invité(e) à [événement] le [date] à [lieu].\n\nDites-nous si vous pouvez venir via le lien ci-dessous.\n\n[noms des hôtes]'
        },
        'general-short': {
          name: 'Courte et amicale', hint: 'Une ligne et un lien.',
          body: 'Bonjour {{first_name}}, c’est [noms des hôtes]. Vous êtes invité(e) à [événement] le [date]. Répondez sur le lien ci-dessous.'
        },
        'rem-nudge': {
          name: 'Petit rappel', hint: 'Le premier rappel. Léger, sans pression.',
          body: 'Bonjour {{first_name}}, c’est [noms des hôtes] — un petit rappel au sujet de [événement]. Nous n’avons pas encore de vos nouvelles et nous aimerions savoir si vous pourrez être des nôtres.\n\nCela ne prend qu’un instant sur le lien ci-dessous.'
        },
        'rem-deadline': {
          name: 'Nous bouclons les effectifs', hint: 'Celui qui obtient vraiment des réponses : il dit pourquoi maintenant.',
          body: 'Bonjour {{first_name}}, nous bouclons les effectifs de [événement] le [date].\n\nSi vous souhaitez vous joindre à nous, dites-le-nous avant via le lien ci-dessous.\n\n[noms des hôtes]'
        },
        'rem-last-call': {
          name: 'Dernier appel', hint: 'Le dernier. Court, et clair sur le fait que c’est le dernier.',
          body: 'Bonjour {{first_name}}, dernier appel — nous clôturons la liste de [événement] [aujourd’hui/demain].\n\nSans réponse de votre part, nous comprendrons que vous ne pouvez pas venir. Le lien est ci-dessous si vous le pouvez.\n\n[noms des hôtes]'
        },
        'rem-day-of': {
          name: 'Le jour même', hint: 'Les horaires et rien d’autre. Envoyé le matin même.',
          body: 'Le grand jour est enfin arrivé !\n\nNous avons hâte de célébrer avec vous.\n\nCérémonie à [heure].\nRéception à [heure].\n\n[noms des hôtes]'
        },
        'rem-practical': {
          name: 'Détails pratiques', hint: 'Un ou deux jours avant, pour ce qu’il faut savoir.',
          body: 'Bonjour {{first_name}}, c’est [noms des hôtes] — à [date] !\n\nOuverture à [heure] à [lieu]. [stationnement / tenue / tout ce qu’il faut savoir].'
        },
        'rem-maybe': {
          name: 'Des nouvelles ?', hint: 'Chaleureux, et explique pourquoi la réponse compte.',
          body: 'Bonjour {{first_name}}, c’est [noms des hôtes] — vous aviez répondu peut-être à [événement]. Des nouvelles ?\n\nNous serions ravis de vous compter parmi nous, et le savoir dans un sens ou dans l’autre nous aide beaucoup. Le lien ci-dessous est toujours ouvert.'
        },
        'rem-declined-warm': {
          name: 'Vous nous manquerez', hint: 'Envoyé le jour même à ceux qui ne peuvent pas venir.',
          body: 'Nous sommes vraiment désolés que vous ne puissiez pas être là, mais nous espérons que vous fêterez cela avec nous à distance.\n\n[lien de la retransmission / comment y participer]\n\n[noms des hôtes]'
        },
        'rem-thank-you': {
          name: 'Merci, après coup', hint: 'Le lendemain. Ne coûte rien et marque toujours.',
          body: 'Un immense merci à tous ceux qui ont célébré avec nous, sur place et à distance.\n\nCela comptait énormément pour nous, et nous avons hâte de recommencer.\n\n[noms des hôtes]'
        },
        'rem-change': {
          name: 'Quelque chose a changé', hint: 'Pour un changement d’heure, de lieu ou de programme.',
          body: 'Bonjour {{first_name}}, c’est [noms des hôtes] — une mise à jour au sujet de [événement] :\n\n[ce qui a changé]\n\nTout le reste est inchangé. Désolés pour le court délai !'
        }
      }
    },

    de: {
      ui: {
        title: 'Nachrichtenvorlagen',
        reminderTitle: 'Erinnerungsvorlagen',
        lede: 'Wähle einen Ausgangspunkt und passe ihn an — das Hervorgehobene füllst du selbst aus.',
        use: 'Verwenden',
        replace: 'Den geschriebenen Text durch diese Vorlage ersetzen?',
        blanksLead: 'Noch auszufüllen:',
        andMore: 'und {n} weitere',
        allSections: 'Alle',
        fillTitle: "Angaben ausfüllen",
        fillLede: "Fülle die Angaben unten aus, um diese Nachricht zu personalisieren.",
        insert: "Nachricht einfügen",
        back: "Zurück zu den Vorlagen",
        previewLabel: "Vorschau",
      },
      families: { wedding: 'Hochzeit', milestone: 'Besonderer Anlass', celebration: 'Feier', corporate: 'Geschäftlich', general: 'Jedes Event' },
      audiences: {
        no_response: 'Gäste ohne Antwort',
        attending: 'Gäste, die kommen',
        maybe: 'Gäste mit Vielleicht',
        declined: 'Gäste, die nicht können',
        all: 'Alle Gäste'
      },
      t: {
        'wedding-formal': {
          name: 'Formelle Einladung', hint: 'Die klassische Form: wer, wann und wohin antworten.',
          body: 'Liebe/r {{name}},\n\nwir laden dich herzlich zur Hochzeit von [Namen] am [Datum] in [Ort] ein.\n\nZum Antworten und für alle Details tippe bitte auf den Link unten.\n\nHerzlichst,\n[Namen]'
        },
        'wedding-warm': {
          name: 'Herzlich und kurz', hint: 'Für eine Gästeliste aus Freunden, nicht für eine formelle.',
          body: 'Hallo {{first_name}}, hier sind [Namen] — wir heiraten!\n\nWir würden uns sehr freuen, wenn du dabei bist. Zeiten, Anfahrt und Zusage findest du über den Link unten.'
        },
        'wedding-save-the-date': {
          name: 'Save the Date', hint: 'Früh verschickt, vor der eigentlichen Einladung.',
          body: 'Hallo {{first_name}}, halte dir den Termin frei: [Namen] heiraten am [Datum].\n\nDie richtige Einladung folgt, aber sag uns gern jetzt schon Bescheid, ob du kommen kannst.\n\n[Namen]'
        },
        'milestone-formal': {
          name: 'Formelle Einladung', hint: 'Nennt den Anlass und die gefeierte Person.',
          body: 'Liebe/r {{name}},\n\nwir laden dich ein, [Anlass] von [Name] am [Datum] in [Ort] mitzufeiern.\n\nZum Antworten und für alle Details tippe bitte auf den Link unten.\n\n[Namen der Gastgeber]'
        },
        'milestone-warm': {
          name: 'Herzlich und kurz', hint: 'Freundlich und schnell auf dem Handy gelesen.',
          body: 'Hallo {{first_name}}, hier sind [Namen der Gastgeber] — wir feiern [Anlass] von [Name] am [Datum] und würden dich gern dabei haben.\n\nAlles Weitere findest du über den Link unten.'
        },
        'celebration-invite': {
          name: 'Einladung zur Feier', hint: 'Die Details zuerst, die Zusage über den Link.',
          body: 'Hallo {{first_name}}, du bist eingeladen!\n\n[Anlass] am [Datum] in [Ort], ab [Uhrzeit].\n\nSag uns über den Link unten Bescheid, ob du kommst.\n\n[Namen der Gastgeber]'
        },
        'celebration-short': {
          name: 'Kurz und freundlich', hint: 'Zwei Zeilen. Ideal, wenn alle schon wissen, worum es geht.',
          body: 'Hallo {{first_name}} — [Anlass], [Datum], [Ort]. Komm vorbei!\n\nZusagen über den Link unten.\n\n[Namen der Gastgeber]'
        },
        'corporate-formal': {
          name: 'Formelle Einladung', hint: 'Sachlich und präzise, für eine berufliche Liste.',
          body: 'Sehr geehrte/r {{name}},\n\nwir laden Sie herzlich zu [Veranstaltung] am [Datum] in [Ort] ein.\n\nBitte bestätigen Sie Ihre Teilnahme über den Link unten.\n\n[Ihr Name]\n[Unternehmen]'
        },
        'corporate-brief': {
          name: 'Kurz und direkt', hint: 'Für eine Liste, die die Veranstaltung schon kennt.',
          body: 'Hallo {{first_name}},\n\n[Veranstaltung] findet am [Datum] in [Ort] statt. Bitte melden Sie sich über den Link unten an — die Plätze sind begrenzt.\n\n[Unternehmen]'
        },
        'general-invite': {
          name: 'Einfache Einladung', hint: 'Passt für alles.',
          body: 'Hallo {{name}},\n\ndu bist zu [Veranstaltung] am [Datum] in [Ort] eingeladen.\n\nSag uns über den Link unten Bescheid, ob du kommen kannst.\n\n[Namen der Gastgeber]'
        },
        'general-short': {
          name: 'Kurz und freundlich', hint: 'Eine Zeile und ein Link.',
          body: 'Hallo {{first_name}}, hier sind [Namen der Gastgeber]. Du bist zu [Veranstaltung] am [Datum] eingeladen. Zusagen über den Link unten.'
        },
        'rem-nudge': {
          name: 'Freundliche Erinnerung', hint: 'Die erste Erinnerung. Leicht, ohne Druck.',
          body: 'Hallo {{first_name}}, hier sind [Namen der Gastgeber] — eine kurze Erinnerung wegen [Veranstaltung]. Wir haben noch nichts von dir gehört und würden gern wissen, ob du dabei bist.\n\nEs dauert nur einen Moment über den Link unten.'
        },
        'rem-deadline': {
          name: 'Wir schließen die Zahlen ab', hint: 'Die, die wirklich Antworten bringt — sie sagt, warum jetzt.',
          body: 'Hallo {{first_name}}, wir schließen die Gästezahl für [Veranstaltung] am [Datum] ab.\n\nWenn du dabei sein möchtest, sag uns bitte vorher über den Link unten Bescheid.\n\n[Namen der Gastgeber]'
        },
        'rem-last-call': {
          name: 'Letzter Aufruf', hint: 'Die letzte. Kurz, und klar, dass es die letzte ist.',
          body: 'Hallo {{first_name}}, letzter Aufruf — wir schließen die Gästeliste für [Veranstaltung] [heute/morgen].\n\nOhne Rückmeldung gehen wir davon aus, dass du nicht kannst. Der Link ist unten, falls doch.\n\n[Namen der Gastgeber]'
        },
        'rem-day-of': {
          name: 'Der Tag selbst', hint: 'Nur die Zeiten. Am Morgen des Tages verschickt.',
          body: 'Der große Tag ist endlich da!\n\nWir freuen uns sehr, mit dir zu feiern.\n\nZeremonie um [Uhrzeit].\nEmpfang um [Uhrzeit].\n\n[Namen der Gastgeber]'
        },
        'rem-practical': {
          name: 'Praktische Hinweise', hint: 'Ein oder zwei Tage vorher, für alles Wissenswerte.',
          body: 'Hallo {{first_name}}, hier sind [Namen der Gastgeber] — bis [Datum]!\n\nEinlass ab [Uhrzeit] in [Ort]. [Parken / Dresscode / was sonst wichtig ist].'
        },
        'rem-maybe': {
          name: 'Schon entschieden?', hint: 'Herzlich, und erklärt, warum die Antwort zählt.',
          body: 'Hallo {{first_name}}, hier sind [Namen der Gastgeber] — du hattest bei [Veranstaltung] mit Vielleicht geantwortet. Schon entschieden?\n\nWir hätten dich sehr gern dabei, und eine klare Antwort hilft uns bei der Planung enorm. Der Link unten ist weiterhin offen.'
        },
        'rem-declined-warm': {
          name: 'Schade, dass du fehlst', hint: 'Am Tag selbst an alle, die nicht können.',
          body: 'Schade, dass du nicht dabei sein kannst — wir hoffen, du feierst aus der Ferne mit uns.\n\n[Livestream-Link / wie sie dabei sein können]\n\n[Namen der Gastgeber]'
        },
        'rem-thank-you': {
          name: 'Danke, im Nachhinein', hint: 'Am Tag danach. Kostet nichts und bleibt immer in Erinnerung.',
          body: 'Ganz herzlichen Dank an alle, die mit uns gefeiert haben — vor Ort und aus der Ferne.\n\nEs hat uns unglaublich viel bedeutet, und wir freuen uns schon aufs nächste Mal.\n\n[Namen der Gastgeber]'
        },
        'rem-change': {
          name: 'Es hat sich etwas geändert', hint: 'Für eine Änderung von Zeit, Ort oder Ablauf.',
          body: 'Hallo {{first_name}}, hier sind [Namen der Gastgeber] — eine Änderung zu [Veranstaltung]:\n\n[was sich geändert hat]\n\nAlles andere bleibt wie geplant. Entschuldigt die kurzfristige Info!'
        }
      }
    },

    pt: {
      ui: {
        title: 'Modelos de mensagem',
        reminderTitle: 'Modelos de lembrete',
        lede: 'Escolha um ponto de partida e edite — o que está destacado é seu para preencher.',
        use: 'Usar este',
        replace: 'Substituir o que você escreveu por este modelo?',
        blanksLead: 'Ainda falta preencher:',
        andMore: 'e mais {n}',
        allSections: 'Todos',
        fillTitle: "Preencha os detalhes",
        fillLede: "Preencha os detalhes abaixo para personalizar esta mensagem.",
        insert: "Inserir mensagem",
        back: "Voltar aos modelos",
        previewLabel: "Pré-visualização",
      },
      families: { wedding: 'Casamento', milestone: 'Data especial', celebration: 'Festa', corporate: 'Corporativo', general: 'Qualquer evento' },
      audiences: {
        no_response: 'Convidados que não responderam',
        attending: 'Convidados que vão',
        maybe: 'Convidados que disseram talvez',
        declined: 'Convidados que não podem ir',
        all: 'Qualquer público'
      },
      t: {
        'wedding-formal': {
          name: 'Convite formal', hint: 'A forma clássica: quem, quando e onde responder.',
          body: 'Prezado(a) {{name}},\n\nVocê está convidado(a) para o casamento de [nomes] no dia [data] em [local].\n\nPara confirmar e ver todos os detalhes, toque no link abaixo.\n\nCom carinho,\n[nomes]'
        },
        'wedding-warm': {
          name: 'Calorosa e curta', hint: 'Para uma lista de amigos, não uma formal.',
          body: 'Oi {{first_name}}, aqui é [nomes] — vamos nos casar!\n\nAdoraríamos ter você lá. Horários, como chegar e confirmação estão no link abaixo.'
        },
        'wedding-save-the-date': {
          name: 'Salve a data', hint: 'Enviado cedo, antes do convite completo.',
          body: 'Oi {{first_name}}, salve a data: [nomes] vão se casar no dia [data].\n\nO convite completo vem depois, mas já nos conte se acha que consegue vir.\n\n[nomes]'
        },
        'milestone-formal': {
          name: 'Convite formal', hint: 'Nomeia a ocasião e a pessoa homenageada.',
          body: 'Prezado(a) {{name}},\n\nVocê está convidado(a) para celebrar [ocasião] de [nome] no dia [data] em [local].\n\nPara confirmar e ver todos os detalhes, toque no link abaixo.\n\n[nomes dos anfitriões]'
        },
        'milestone-warm': {
          name: 'Calorosa e curta', hint: 'Amigável e rápida de ler no celular.',
          body: 'Oi {{first_name}}, aqui é [nomes dos anfitriões] — vamos celebrar [ocasião] de [nome] no dia [data] e adoraríamos ter você lá.\n\nTudo o que você precisa está no link abaixo.'
        },
        'celebration-invite': {
          name: 'Convite de festa', hint: 'Os detalhes primeiro, a confirmação no link.',
          body: 'Oi {{first_name}}, você está convidado(a)!\n\n[ocasião] no dia [data] em [local], a partir das [hora].\n\nNos avise se puder vir pelo link abaixo.\n\n[nomes dos anfitriões]'
        },
        'celebration-short': {
          name: 'Curta e amigável', hint: 'Duas linhas. Ideal quando todos já sabem do que se trata.',
          body: 'Oi {{first_name}} — [ocasião], [data], [local]. Vem!\n\nConfirme no link abaixo.\n\n[nomes dos anfitriões]'
        },
        'corporate-formal': {
          name: 'Convite formal', hint: 'Neutro e preciso, para uma lista profissional.',
          body: 'Prezado(a) {{name}},\n\nTemos o prazer de convidá-lo(a) para [evento] no dia [data] em [local].\n\nPor favor, confirme sua presença pelo link abaixo.\n\n[seu nome]\n[empresa]'
        },
        'corporate-brief': {
          name: 'Curto e direto', hint: 'Para uma lista que já conhece o evento.',
          body: 'Olá {{first_name}},\n\n[evento] acontece no dia [data] em [local]. Inscreva-se pelo link abaixo — as vagas são limitadas.\n\n[empresa]'
        },
        'general-invite': {
          name: 'Convite simples', hint: 'Serve para qualquer coisa.',
          body: 'Oi {{name}},\n\nVocê está convidado(a) para [evento] no dia [data] em [local].\n\nNos avise se puder vir pelo link abaixo.\n\n[nomes dos anfitriões]'
        },
        'general-short': {
          name: 'Curta e amigável', hint: 'Uma linha e um link.',
          body: 'Oi {{first_name}}, aqui é [nomes dos anfitriões]. Você está convidado(a) para [evento] no dia [data]. Confirme no link abaixo.'
        },
        'rem-nudge': {
          name: 'Lembrete gentil', hint: 'O primeiro lembrete. Leve, sem pressão.',
          body: 'Oi {{first_name}}, aqui é [nomes dos anfitriões] — um lembrete rápido sobre [evento]. Ainda não recebemos sua resposta e adoraríamos saber se você pode vir.\n\nLeva só um instante no link abaixo.'
        },
        'rem-deadline': {
          name: 'Fechando os números', hint: 'O que realmente traz respostas: diz por que agora.',
          body: 'Oi {{first_name}}, estamos fechando o número de convidados de [evento] no dia [data].\n\nSe quiser vir, nos avise antes disso pelo link abaixo.\n\n[nomes dos anfitriões]'
        },
        'rem-last-call': {
          name: 'Último aviso', hint: 'O último. Curto, e claro que é o último.',
          body: 'Oi {{first_name}}, último aviso — fechamos a lista de [evento] [hoje/amanhã].\n\nSem resposta, vamos entender que você não pode vir. O link está abaixo se puder.\n\n[nomes dos anfitriões]'
        },
        'rem-day-of': {
          name: 'O dia chegou', hint: 'Horários e nada mais. Enviado na manhã do dia.',
          body: 'O grande dia finalmente chegou!\n\nEstamos muito animados para celebrar com você.\n\nCerimônia às [hora].\nRecepção às [hora].\n\n[nomes dos anfitriões]'
        },
        'rem-practical': {
          name: 'Detalhes práticos', hint: 'Um ou dois dias antes, para o que precisam saber.',
          body: 'Oi {{first_name}}, aqui é [nomes dos anfitriões] — até dia [data]!\n\nAbertura às [hora] em [local]. [estacionamento / traje / o que mais precisarem saber].'
        },
        'rem-maybe': {
          name: 'Alguma novidade?', hint: 'Caloroso, e explica por que a resposta importa.',
          body: 'Oi {{first_name}}, aqui é [nomes dos anfitriões] — você disse talvez para [evento]. Alguma novidade?\n\nAdoraríamos ter você, e saber de um jeito ou de outro ajuda muito a organizar. O link abaixo continua aberto.'
        },
        'rem-declined-warm': {
          name: 'Vamos sentir sua falta', hint: 'Enviado no dia, para quem não pode ir.',
          body: 'Sentimos muito que você não possa estar conosco pessoalmente, mas esperamos que celebre com a gente de longe.\n\n[link da transmissão / como podem participar]\n\n[nomes dos anfitriões]'
        },
        'rem-thank-you': {
          name: 'Obrigado, depois', hint: 'No dia seguinte. Não custa nada e é sempre lembrado.',
          body: 'Muito obrigado a todos que celebraram com a gente, de perto e de longe.\n\nSignificou muito para nós, e mal podemos esperar para celebrar juntos de novo.\n\n[nomes dos anfitriões]'
        },
        'rem-change': {
          name: 'Algo mudou', hint: 'Para mudança de horário, local ou programação.',
          body: 'Oi {{first_name}}, aqui é [nomes dos anfitriões] — uma novidade sobre [evento]:\n\n[o que mudou]\n\nTodo o resto continua igual. Desculpe o aviso em cima da hora!'
        }
      }
    },

    he: {
      ui: {
        title: 'תבניות הודעה',
        reminderTitle: 'תבניות תזכורת',
        lede: 'בחרו נקודת פתיחה וערכו אותה — מה שמסומן זה מה שנשאר לכם למלא.',
        use: 'השתמשו בזו',
        replace: 'להחליף את מה שכתבתם בתבנית הזו?',
        blanksLead: 'נשאר למלא:',
        andMore: 'ועוד {n}',
        allSections: 'הכול',
        fillTitle: "מלאו את הפרטים",
        fillLede: "מלאו את הפרטים למטה כדי להתאים אישית את ההודעה.",
        insert: "הכניסו את ההודעה",
        back: "חזרה לתבניות",
        previewLabel: "תצוגה מקדימה",
      },
      families: { wedding: 'חתונה', milestone: 'אירוע מיוחד', celebration: 'מסיבה', corporate: 'עסקי', general: 'כל אירוע' },
      audiences: {
        no_response: 'אורחים שלא השיבו',
        attending: 'אורחים שמגיעים',
        maybe: 'אורחים שענו אולי',
        declined: 'אורחים שלא יכולים להגיע',
        all: 'כל האורחים'
      },
      t: {
        'wedding-formal': {
          name: 'הזמנה רשמית', hint: 'המבנה הקלאסי — מי, מתי, ואיפה להשיב.',
          body: '{{name}} היקר/ה,\n\nאתם מוזמנים לחתונה של [שמות] בתאריך [תאריך] ב[מקום].\n\nלאישור הגעה ולכל הפרטים, הקישו על הקישור למטה.\n\nבאהבה,\n[שמות]'
        },
        'wedding-warm': {
          name: 'חמה וקצרה', hint: 'לרשימת חברים, לא לרשימה רשמית.',
          body: 'היי {{first_name}}, כאן [שמות] — אנחנו מתחתנים!\n\nנשמח מאוד שתהיו איתנו. שעות, הגעה ואישור — הכול בקישור למטה.'
        },
        'wedding-save-the-date': {
          name: 'שמרו את התאריך', hint: 'נשלח מוקדם, לפני ההזמנה המלאה.',
          body: 'היי {{first_name}}, שמרו את התאריך — [שמות] מתחתנים בתאריך [תאריך].\n\nההזמנה המלאה בדרך, אבל נשמח לדעת כבר עכשיו אם נראה לכם שתגיעו.\n\n[שמות]'
        },
        'milestone-formal': {
          name: 'הזמנה רשמית', hint: 'מציינת את האירוע ואת מי חוגגים.',
          body: '{{name}} היקר/ה,\n\nאתם מוזמנים לחגוג את [האירוע] של [שם] בתאריך [תאריך] ב[מקום].\n\nלאישור הגעה ולכל הפרטים, הקישו על הקישור למטה.\n\n[שמות המארחים]'
        },
        'milestone-warm': {
          name: 'חמה וקצרה', hint: 'ידידותית ונקראת מהר בנייד.',
          body: 'היי {{first_name}}, כאן [שמות המארחים] — אנחנו חוגגים את [האירוע] של [שם] בתאריך [תאריך] ונשמח שתהיו איתנו.\n\nכל מה שצריך נמצא בקישור למטה.'
        },
        'celebration-invite': {
          name: 'הזמנה למסיבה', hint: 'הפרטים קודם, אישור ההגעה בקישור.',
          body: 'היי {{first_name}}, אתם מוזמנים!\n\n[האירוע] בתאריך [תאריך] ב[מקום], החל מ[שעה].\n\nספרו לנו אם אתם מגיעים בקישור למטה.\n\n[שמות המארחים]'
        },
        'celebration-short': {
          name: 'קצרה וידידותית', hint: 'שתי שורות. מצוין כשכולם כבר יודעים במה מדובר.',
          body: 'היי {{first_name}} — [האירוע], [תאריך], [מקום]. בואו!\n\nאישור הגעה בקישור למטה.\n\n[שמות המארחים]'
        },
        'corporate-formal': {
          name: 'הזמנה רשמית', hint: 'עניינית ומדויקת, לרשימה מקצועית.',
          body: '{{name}} שלום,\n\nשמחים להזמין אותך ל[אירוע] בתאריך [תאריך] ב[מקום].\n\nנא לאשר הגעה בקישור למטה.\n\n[השם שלך]\n[שם החברה]'
        },
        'corporate-brief': {
          name: 'קצרה וישירה', hint: 'לרשימה שכבר מכירה את האירוע.',
          body: 'שלום {{first_name}},\n\n[אירוע] יתקיים בתאריך [תאריך] ב[מקום]. נא להירשם בקישור למטה — מספר המקומות מוגבל.\n\n[שם החברה]'
        },
        'general-invite': {
          name: 'הזמנה פשוטה', hint: 'מתאימה לכל דבר.',
          body: 'היי {{name}},\n\nאתם מוזמנים ל[אירוע] בתאריך [תאריך] ב[מקום].\n\nספרו לנו אם תוכלו להגיע בקישור למטה.\n\n[שמות המארחים]'
        },
        'general-short': {
          name: 'קצרה וידידותית', hint: 'שורה אחת וקישור.',
          body: 'היי {{first_name}}, כאן [שמות המארחים]. אתם מוזמנים ל[אירוע] בתאריך [תאריך]. אישור הגעה בקישור למטה.'
        },
        'rem-nudge': {
          name: 'תזכורת עדינה', hint: 'התזכורת הראשונה. קלה, בלי לחץ.',
          body: 'היי {{first_name}}, כאן [שמות המארחים] — תזכורת קטנה לגבי [אירוע]. עוד לא שמענו מכם ונשמח לדעת אם תוכלו להגיע.\n\nזה לוקח רגע בקישור למטה.'
        },
        'rem-deadline': {
          name: 'סוגרים מספרים', hint: 'זו שבאמת מביאה תשובות — היא מסבירה למה עכשיו.',
          body: 'היי {{first_name}}, אנחנו סוגרים את מספר האורחים ל[אירוע] בתאריך [תאריך].\n\nאם תרצו להצטרף, ספרו לנו לפני כן בקישור למטה.\n\n[שמות המארחים]'
        },
        'rem-last-call': {
          name: 'קריאה אחרונה', hint: 'האחרונה. קצרה, וברור שזו האחרונה.',
          body: 'היי {{first_name}}, קריאה אחרונה — אנחנו סוגרים את רשימת האורחים ל[אירוע] [היום/מחר].\n\nאם לא נשמע מכם נבין שלא תוכלו להגיע. הקישור למטה אם כן.\n\n[שמות המארחים]'
        },
        'rem-day-of': {
          name: 'היום הגדול', hint: 'שעות ותו לא. נשלח בבוקר האירוע.',
          body: 'היום הגדול סוף סוף כאן!\n\nאנחנו מתרגשים לחגוג אתכם.\n\nחופה בשעה [שעה].\nקבלת פנים בשעה [שעה].\n\n[שמות המארחים]'
        },
        'rem-practical': {
          name: 'פרטים פרקטיים', hint: 'יום-יומיים לפני, לכל מה שצריך לדעת.',
          body: 'היי {{first_name}}, כאן [שמות המארחים] — נתראה ב[תאריך]!\n\nהדלתות נפתחות ב[שעה] ב[מקום]. [חניה / קוד לבוש / כל מה שחשוב לדעת].'
        },
        'rem-maybe': {
          name: 'יש חדש?', hint: 'חמה, ומסבירה למה התשובה חשובה.',
          body: 'היי {{first_name}}, כאן [שמות המארחים] — ענית אולי ל[אירוע]. יש חדש?\n\nנשמח מאוד שתהיו איתנו, ותשובה ברורה לכאן או לכאן עוזרת לנו מאוד בתכנון. הקישור למטה עדיין פתוח.'
        },
        'rem-declined-warm': {
          name: 'חבל שלא תהיו', hint: 'נשלח ביום האירוע למי שלא יכולים להגיע.',
          body: 'חבל מאוד שלא תוכלו להיות איתנו, אבל אנחנו מקווים שתחגגו איתנו מרחוק.\n\n[קישור לשידור / איך אפשר להשתתף]\n\n[שמות המארחים]'
        },
        'rem-thank-you': {
          name: 'תודה, אחרי', hint: 'ביום שאחרי. לא עולה כלום ותמיד זוכרים אותה.',
          body: 'תודה ענקית לכל מי שחגג איתנו, מקרוב ומרחוק.\n\nזה היה המון בשבילנו, ואנחנו כבר מחכים לחגוג יחד שוב.\n\n[שמות המארחים]'
        },
        'rem-change': {
          name: 'משהו השתנה', hint: 'לשינוי בשעה, במקום או בתוכנית.',
          body: 'היי {{first_name}}, כאן [שמות המארחים] — עדכון אחד לגבי [אירוע]:\n\n[מה השתנה]\n\nכל השאר נשאר כמו שתוכנן. סליחה על ההתראה הקצרה!'
        }
      }
    },

    ar: {
      ui: {
        title: 'قوالب الرسائل',
        reminderTitle: 'قوالب التذكير',
        lede: 'اختر نقطة بداية وعدّلها — ما هو مظلَّل متروك لك لتعبئته.',
        use: 'استخدم هذا',
        replace: 'هل تستبدل ما كتبته بهذا القالب؟',
        blanksLead: 'ما زال عليك تعبئة:',
        andMore: 'و{n} أخرى',
        allSections: 'الكل',
        fillTitle: "أكمل التفاصيل",
        fillLede: "املأ التفاصيل أدناه لتخصيص هذه الرسالة.",
        insert: "إدراج الرسالة",
        back: "العودة إلى القوالب",
        previewLabel: "معاينة",
      },
      families: { wedding: 'زفاف', milestone: 'مناسبة خاصة', celebration: 'حفلة', corporate: 'مؤسسي', general: 'أي مناسبة' },
      audiences: {
        no_response: 'الضيوف الذين لم يردّوا',
        attending: 'الضيوف الحاضرون',
        maybe: 'الضيوف الذين قالوا ربما',
        declined: 'الضيوف الذين لا يستطيعون الحضور',
        all: 'كل الضيوف'
      },
      t: {
        'wedding-formal': {
          name: 'دعوة رسمية', hint: 'الشكل الكلاسيكي: من ومتى وأين تردّ.',
          body: 'عزيزي/عزيزتي {{name}}،\n\nندعوك لحضور زفاف [الأسماء] بتاريخ [التاريخ] في [المكان].\n\nلتأكيد الحضور والاطلاع على كل التفاصيل، اضغط على الرابط أدناه.\n\nمع المحبة،\n[الأسماء]'
        },
        'wedding-warm': {
          name: 'ودّية وقصيرة', hint: 'لقائمة من الأصدقاء لا لقائمة رسمية.',
          body: 'مرحباً {{first_name}}، معك [الأسماء] — نحن نتزوج!\n\nيسعدنا كثيراً أن تكون معنا. المواعيد والوصول وتأكيد الحضور كلها على الرابط أدناه.'
        },
        'wedding-save-the-date': {
          name: 'احجز التاريخ', hint: 'تُرسل مبكراً، قبل الدعوة الكاملة.',
          body: 'مرحباً {{first_name}}، احجز التاريخ — [الأسماء] سيتزوجان بتاريخ [التاريخ].\n\nالدعوة الكاملة ستصلك لاحقاً، لكن أخبرنا من الآن إن كنت تظن أنك ستتمكن من الحضور.\n\n[الأسماء]'
        },
        'milestone-formal': {
          name: 'دعوة رسمية', hint: 'تذكر المناسبة والشخص المحتفى به.',
          body: 'عزيزي/عزيزتي {{name}}،\n\nندعوك للاحتفال بـ[المناسبة] الخاصة بـ[الاسم] بتاريخ [التاريخ] في [المكان].\n\nلتأكيد الحضور والاطلاع على كل التفاصيل، اضغط على الرابط أدناه.\n\n[أسماء المضيفين]'
        },
        'milestone-warm': {
          name: 'ودّية وقصيرة', hint: 'لطيفة وسريعة القراءة على الهاتف.',
          body: 'مرحباً {{first_name}}، معك [أسماء المضيفين] — نحتفل بـ[المناسبة] الخاصة بـ[الاسم] بتاريخ [التاريخ] ويسعدنا وجودك.\n\nكل ما تحتاجه على الرابط أدناه.'
        },
        'celebration-invite': {
          name: 'دعوة لحفلة', hint: 'التفاصيل أولاً، وتأكيد الحضور على الرابط.',
          body: 'مرحباً {{first_name}}، أنت مدعو!\n\n[المناسبة] بتاريخ [التاريخ] في [المكان]، ابتداءً من [الوقت].\n\nأخبرنا إن كنت ستحضر عبر الرابط أدناه.\n\n[أسماء المضيفين]'
        },
        'celebration-short': {
          name: 'قصيرة وودّية', hint: 'سطران. مثالية حين يعرف الجميع الأمر أصلاً.',
          body: 'مرحباً {{first_name}} — [المناسبة]، [التاريخ]، [المكان]. تعال!\n\nأكّد حضورك عبر الرابط أدناه.\n\n[أسماء المضيفين]'
        },
        'corporate-formal': {
          name: 'دعوة رسمية', hint: 'محايدة ودقيقة، لقائمة مهنية.',
          body: 'عزيزي/عزيزتي {{name}}،\n\nيسرّنا دعوتك إلى [الفعالية] بتاريخ [التاريخ] في [المكان].\n\nيرجى تأكيد حضورك عبر الرابط أدناه.\n\n[اسمك]\n[اسم الشركة]'
        },
        'corporate-brief': {
          name: 'قصيرة ومباشرة', hint: 'لقائمة تعرف الفعالية مسبقاً.',
          body: 'مرحباً {{first_name}}،\n\nتقام [الفعالية] بتاريخ [التاريخ] في [المكان]. يرجى التسجيل عبر الرابط أدناه — المقاعد محدودة.\n\n[اسم الشركة]'
        },
        'general-invite': {
          name: 'دعوة بسيطة', hint: 'تصلح لكل شيء.',
          body: 'مرحباً {{name}}،\n\nأنت مدعو إلى [الفعالية] بتاريخ [التاريخ] في [المكان].\n\nأخبرنا إن كنت تستطيع الحضور عبر الرابط أدناه.\n\n[أسماء المضيفين]'
        },
        'general-short': {
          name: 'قصيرة وودّية', hint: 'سطر واحد ورابط.',
          body: 'مرحباً {{first_name}}، معك [أسماء المضيفين]. أنت مدعو إلى [الفعالية] بتاريخ [التاريخ]. أكّد حضورك عبر الرابط أدناه.'
        },
        'rem-nudge': {
          name: 'تذكير لطيف', hint: 'التذكير الأول. خفيف وبلا ضغط.',
          body: 'مرحباً {{first_name}}، معك [أسماء المضيفين] — تذكير سريع بخصوص [الفعالية]. لم يصلنا ردّك بعد ويسعدنا أن نعرف إن كنت ستنضم إلينا.\n\nلن يستغرق الأمر سوى لحظة عبر الرابط أدناه.'
        },
        'rem-deadline': {
          name: 'نغلق الأعداد قريباً', hint: 'الرسالة التي تجلب الردود فعلاً — تقول لماذا الآن.',
          body: 'مرحباً {{first_name}}، نحن بصدد إغلاق عدد الضيوف لـ[الفعالية] بتاريخ [التاريخ].\n\nإن أردت الانضمام إلينا، أخبرنا قبل ذلك عبر الرابط أدناه.\n\n[أسماء المضيفين]'
        },
        'rem-last-call': {
          name: 'النداء الأخير', hint: 'الأخيرة. قصيرة وواضحة أنها الأخيرة.',
          body: 'مرحباً {{first_name}}، النداء الأخير — نغلق قائمة ضيوف [الفعالية] [اليوم/غداً].\n\nإن لم يصلنا ردّك سنفترض أنك لا تستطيع الحضور. الرابط أدناه إن استطعت.\n\n[أسماء المضيفين]'
        },
        'rem-day-of': {
          name: 'اليوم الموعود', hint: 'المواعيد فقط. تُرسل صباح اليوم نفسه.',
          body: 'وصل اليوم الكبير أخيراً!\n\nنحن متحمسون للاحتفال معك.\n\nالمراسم في [الوقت].\nالاستقبال في [الوقت].\n\n[أسماء المضيفين]'
        },
        'rem-practical': {
          name: 'تفاصيل عملية', hint: 'قبل يوم أو يومين، لكل ما يحتاجون معرفته.',
          body: 'مرحباً {{first_name}}، معك [أسماء المضيفين] — نراك بتاريخ [التاريخ]!\n\nتُفتح الأبواب في [الوقت] في [المكان]. [المواقف / اللباس / أي شيء آخر يحتاجون معرفته].'
        },
        'rem-maybe': {
          name: 'هل من جديد؟', hint: 'ودّية، وتشرح لماذا تهمّ الإجابة.',
          body: 'مرحباً {{first_name}}، معك [أسماء المضيفين] — قلت ربما بخصوص [الفعالية]. هل من جديد؟\n\nيسعدنا وجودك، ومعرفة الإجابة بأي اتجاه تساعدنا كثيراً في التنظيم. الرابط أدناه ما زال مفتوحاً.'
        },
        'rem-declined-warm': {
          name: 'سنفتقدك', hint: 'تُرسل في اليوم نفسه لمن لا يستطيعون الحضور.',
          body: 'يؤسفنا كثيراً أنك لن تكون معنا شخصياً، لكننا نأمل أن تحتفل معنا عن بُعد.\n\n[رابط البث / كيف يمكنهم المشاركة]\n\n[أسماء المضيفين]'
        },
        'rem-thank-you': {
          name: 'شكراً، بعد المناسبة', hint: 'في اليوم التالي. لا تكلّف شيئاً وتبقى في الذاكرة دائماً.',
          body: 'شكراً جزيلاً لكل من احتفل معنا، حضوراً وعن بُعد.\n\nكان ذلك يعني لنا الكثير، ونتطلع للاحتفال معاً من جديد.\n\n[أسماء المضيفين]'
        },
        'rem-change': {
          name: 'طرأ تغيير', hint: 'لتغيير في الوقت أو المكان أو البرنامج.',
          body: 'مرحباً {{first_name}}، معك [أسماء المضيفين] — تحديث واحد بخصوص [الفعالية]:\n\n[ما الذي تغيّر]\n\nكل ما عدا ذلك يبقى كما هو. نعتذر عن الإشعار المتأخر!'
        }
      }
    }
  };

  /**
   * The language to write the templates in.
   *
   * The caller passes what it is composing in — a campaign's source language,
   * say. Failing that we read the page: `window.BUILDER_LANGUAGE` in a builder,
   * the dashboard's stored preference otherwise. English is the last resort, and
   * a language with no pack falls back to it too.
   */
  function langFor(preferred) {
    const candidates = [
      preferred,
      window.BUILDER_LANGUAGE,
      window.bulkSharingWizard && window.bulkSharingWizard.dashboardLanguage,
      document.documentElement && document.documentElement.lang
    ];
    for (const c of candidates) {
      const code = String(c || '').trim().slice(0, 2).toLowerCase();
      if (code === 'en') return 'en';
      if (code && TEMPLATE_I18N[code]) return code;
    }
    return 'en';
  }

  function packFor(lang) {
    return TEMPLATE_I18N[lang] || null;
  }

  /** UI strings for the picker itself. */
  function ui(lang) {
    const p = packFor(lang);
    return Object.assign({}, EN_UI, (p && p.ui) || {});
  }

  /**
   * One template in `lang` — English wording wherever a translation is missing,
   * so a gap reads as untranslated text rather than a template that vanished.
   */
  function localize(tpl, lang) {
    const p = packFor(lang);
    const tr = p && p.t && p.t[tpl.id];
    if (!tr) return tpl;
    return {
      id: tpl.id,
      name: tr.name || tpl.name,
      hint: tr.hint || tpl.hint,
      body: tr.body || tpl.body
    };
  }

  function familyLabel(family, lang) {
    const p = packFor(lang);
    return (p && p.families && p.families[family]) || EN_FAMILIES[family] || family;
  }

  function audienceLabel(audience, lang) {
    const p = packFor(lang);
    return (p && p.audiences && p.audiences[audience]) || EN_AUDIENCES[audience] || audience;
  }

  function familyFor(eventType) {
    const key = String(eventType || '').trim().toLowerCase();
    return FAMILY_BY_EVENT_TYPE[key] || 'general';
  }

  /**
   * Invitation templates, most likely first. The event's own family leads;
   * the general ones follow, because an event type is often not set at all and
   * a host whose wedding is filed as "party" should still find the wedding ones.
   */
  function invitations(eventType, preferredLang) {
    const lang = langFor(preferredLang);
    const fam = familyFor(eventType);
    const order = [];
    if (fam !== 'general') order.push(fam);
    order.push('general');
    Object.keys(INVITATION).forEach((k) => { if (!order.includes(k)) order.push(k); });
    return order.map((k) => ({
      family: k,
      label: familyLabel(k, lang),
      templates: (INVITATION[k] || []).map(t => localize(t, lang))
    }));
  }

  /** Reminder templates for the audience the host has chosen. */
  function reminders(audience, preferredLang) {
    const lang = langFor(preferredLang);
    const key = String(audience || '').trim();
    const order = [];
    if (REMINDER[key] && key !== 'all') order.push(key);
    order.push('all');
    Object.keys(REMINDER).forEach((k) => { if (!order.includes(k)) order.push(k); });
    return order.map((k) => ({
      family: k,
      label: audienceLabel(k, lang),
      templates: (REMINDER[k] || []).map(t => localize(t, lang))
    }));
  }

  /** The [bracketed] bits of a message that are still the host's to fill in. */
  function blanks(text) {
    const out = [];
    String(text == null ? '' : text).replace(/\[([^\]\n]{1,60})\]/g, (m) => { out.push(m); return m; });
    return out;
  }

  /**
   * Every blank our own templates use, in every language, lower-cased.
   *
   * The Send check (2026-10-07 wizard review #13) refuses a message that still
   * carries one of THESE, not any bracket at all: a host may write "[RSVP by
   * 1 June]" on purpose, but "[names]" or "[lugar]" left in is our template
   * sent verbatim. Built once, on first use.
   */
  let knownBlankSet = null;
  function knownBlanks() {
    if (knownBlankSet) return knownBlankSet;
    const set = new Set();
    const add = (body) => blanks(body).forEach(b => set.add(b.toLowerCase()));
    [INVITATION, REMINDER].forEach(group => Object.keys(group).forEach(k =>
      (group[k] || []).forEach(tpl => add(tpl.body))));
    Object.keys(TEMPLATE_I18N).forEach(lang => {
      const t = (TEMPLATE_I18N[lang] && TEMPLATE_I18N[lang].t) || {};
      Object.keys(t).forEach(id => add(t[id] && t[id].body));
    });
    knownBlankSet = set;
    return set;
  }

  /** The distinct template blanks still in `text` (see knownBlanks). */
  function unfilledBlanks(text) {
    const known = knownBlanks();
    return uniqueBlanks(text).filter(b => known.has(b.toLowerCase()));
  }

  /**
   * The DISTINCT blanks in a template, in the order they first appear.
   *
   * "[names]" appears twice in the formal wedding invitation — once in the
   * sentence and once in the sign-off — and asking for the same thing twice is
   * how a host ends up signing a message with a different name than the one in
   * it (task #603 round 3).
   */
  function uniqueBlanks(text) {
    const seen = new Set();
    return blanks(text).filter(b => (seen.has(b) ? false : (seen.add(b), true)));
  }

  /**
   * Substitute the answers into the template.
   *
   * A blank left empty is LEFT AS THE BRACKET, on purpose: the message box's
   * "still to fill in" warning is the safety net for a template sent verbatim,
   * and silently deleting an unanswered blank would take that net away and
   * leave a sentence with a hole in it instead.
   */
  function fillBlanks(text, values) {
    const v = values || {};
    return String(text == null ? '' : text).replace(/\[[^\]\n]{1,60}\]/g, (m, at, s) => {
      let answer = String(v[m] == null ? '' : v[m]).trim();
      // "a partir de las [hora]." with "4:30 p. m." must not end in "m.."
      if (answer.endsWith('.') && s.charAt(at + m.length) === '.') answer = answer.slice(0, -1);
      return answer || m;
    });
  }

  /**
   * Answers this event can supply for itself.
   *
   * The date and the venue are already on the form; asking a host to retype
   * them into a message we are composing for that very event is busywork,
   * and a retyped date is a date that can disagree with the event.
   * Anything we cannot answer is simply left for them to fill in.
   */
  function prefillFromEvent(form, lang) {
    const f = form || {};
    const code = langFor(lang);
    const out = {};
    // The venue as the host typed it in Event Location. This read only the
    // address box, which most forms leave empty, so the venue was never filled.
    const place = String(f.eventLocation || f.locationName || f.venueName || f.locationAddress || '').trim();
    if (place) { out['[place]'] = place; out['[venue]'] = place; }
    const raw = f.eventDate;
    if (raw) {
      // eventDate is a naive YYYY-MM-DD. new Date(str) reads that as UTC and
      // can land a day early west of Greenwich, so parse it as local.
      const d = raw instanceof Date ? raw : new Date(String(raw).replace(/-/g, "/"));
      if (!isNaN(d.getTime())) {
        // The host's own language, so a Spanish message gets a Spanish date.
        try {
          out['[date]'] = d.toLocaleDateString(code, { year: 'numeric', month: 'long', day: 'numeric' });
        } catch (_) {
          // The stored day itself: toISOString() of a local midnight is the day before east of Greenwich.
          out['[date]'] = typeof raw === 'string' ? raw.slice(0, 10) : d.toISOString().slice(0, 10);
        }
      }
    }
    // The time box under the date, as guests see it on the form: the host's
    // Time format, else the language's usual clock. A floating wall clock, so
    // it is formatted on a fixed day in UTC.
    const tm = /^(\d{1,2}):(\d{2})/.exec(String(f.eventTime || '').trim());
    if (tm) {
      const opts = { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' };
      if (f.timeFormat === '12h') opts.hour12 = true;
      if (f.timeFormat === '24h') opts.hourCycle = 'h23';
      try {
        out['[time]'] = new Date(Date.UTC(2000, 0, 1, +tm[1], +tm[2])).toLocaleTimeString(code, opts);
      } catch (_) {
        out['[time]'] = `${tm[1].padStart(2, '0')}:${tm[2]}`;
      }
    }
    const event = String(f.eventTitle || f.formTitle || f.title || '').trim();
    if (event) out['[event]'] = event;
    // The same answers under the blank names of the language being written in:
    // a Spanish template asks for [fecha] and [lugar], so keying only the
    // English names left every non-English template with nothing filled in.
    const names = localBlankNames(code);
    Object.keys(out).forEach((k) => { if (names[k] && !out[names[k]]) out[names[k]] = out[k]; });
    return out;
  }

  /**
   * English blank → the same blank in `lang` ("[date]" → "[fecha]"), read off
   * the templates themselves: each translation carries the English template's
   * blanks in the same order. A pair is kept only when every template agrees
   * on it, so a translation that reorders or drops a blank costs that one
   * prefill rather than filling the wrong blank.
   */
  const localBlankCache = {};
  function localBlankNames(lang) {
    if (localBlankCache[lang]) return localBlankCache[lang];
    const p = packFor(lang);
    const seen = {};
    if (p && p.t) {
      [INVITATION, REMINDER].forEach(group => Object.keys(group).forEach(k =>
        (group[k] || []).forEach((tpl) => {
          const tr = p.t[tpl.id];
          if (!tr || !tr.body) return;
          const a = uniqueBlanks(tpl.body), b = uniqueBlanks(tr.body);
          if (a.length !== b.length) return;
          a.forEach((x, i) => { (seen[x] = seen[x] || new Set()).add(b[i]); });
        })));
    }
    const names = {};
    Object.keys(seen).forEach((x) => { if (seen[x].size === 1) names[x] = [...seen[x]][0]; });
    localBlankCache[lang] = names;
    return names;
  }

  /** "[couple's names]" → "Couple's names", for the field's own label. */
  function blankLabel(blank) {
    const inner = String(blank || '').replace(/^\[|\]$/g, '').trim();
    return inner ? inner.charAt(0).toUpperCase() + inner.slice(1) : '';
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function ensureStyles() {
    if (document.getElementById('msg-templates-styles')) return;
    const st = document.createElement('style');
    st.id = 'msg-templates-styles';
    st.textContent = `
      #messageTemplatesModal .modal-content { border: none; border-radius: 18px; box-shadow: 0 20px 50px rgba(16,24,40,0.18); }
      /* Section chips (task #603 round 2). The picker used to stack every
         family one after another, so the wording that suited the event was a
         scroll away from the wording that did not. */
      /* One row per blank: its name on the left, its box on the right (task
         #603 round 4). Stacked, three short answers filled the dialog and
         pushed the preview below the fold — and the preview is the part that
         shows the work. */
      /* One grid for all the rows (task #659 round 2): the name column is as
         wide as the WIDEST name and no wider, so every box sits right beside
         its own title - "Place" was two inches from its box under a fixed
         quarter-width column - while the boxes still line up in one straight
         column down the dialog. fit-content caps a very long blank name at
         two fifths of the row; it wraps rather than shoving the box off.

         fit-content() may NOT be nested inside minmax() - that is invalid
         per the grid spec, so browsers dropped the whole declaration, the grid
         fell back to one implicit column and every title stacked above its own
         box. Round 2 shipped it that way and round 3 measured around it, which
         is how the dialog came back looking exactly like the thing it was
         meant to fix (task #659 round 4). It is a bare track size here. */
      #messageTemplatesModal .mt-fill-fields {
        display: grid; grid-template-columns: fit-content(40%) minmax(0, 1fr);
        column-gap: 0.6rem; row-gap: 0.35rem; align-items: center;
      }
      #messageTemplatesModal .mt-fill-field { display: contents; }
      #messageTemplatesModal .mt-fill-field label {
        margin: 0;
        font-size: 0.78rem; font-weight: 600; color: #6b5b3e;
        overflow-wrap: anywhere;
        /* Titles end where the column ends, so EVERY title sits the same few
           pixels from its own box rather than the short ones trailing off
           (task #659 round 4). 'end' so it flips with RTL. */
        text-align: end;
      }
      #messageTemplatesModal .mt-fill-field input {
        min-width: 0;
        border: 1px solid #ddd4c4; border-radius: 8px; padding: 5px 10px;
        font-size: 16px; /* under 16px iOS zooms the whole dialog on focus */
        background: #fff; color: #2f2a22;
      }
      /* Below ~420px there is no room for two columns; stack rather than
         squeeze the box to nothing. */
      @media (max-width: 420px) {
        #messageTemplatesModal .mt-fill-fields { grid-template-columns: minmax(0, 1fr); row-gap: 0.15rem; }
        /* Stacked, a title sits ABOVE its box, so it reads from the start edge. */
        #messageTemplatesModal .mt-fill-field label { text-align: start; }
        #messageTemplatesModal .mt-fill-field input { margin-bottom: 0.4rem; }
      }
      #messageTemplatesModal .mt-fill-field input:focus {
        outline: none; border-color: #6b5b3e; box-shadow: 0 0 0 3px rgba(107,91,62,0.12);
      }
      #messageTemplatesModal .mt-chips {
        display: flex; flex-wrap: wrap; gap: 0.35rem; margin-bottom: 0.9rem;
      }
      #messageTemplatesModal .mt-chip {
        font-size: 0.76rem; border: 1px solid #ddd4c4; background: #fff; color: #6b5b3e;
        border-radius: 999px; padding: 3px 12px; cursor: pointer; white-space: nowrap;
      }
      #messageTemplatesModal .mt-chip:hover { background: #f7f4ee; }
      #messageTemplatesModal .mt-chip.is-on { background: #6b5b3e; border-color: #6b5b3e; color: #fff; }
      #messageTemplatesModal .mt-group-label {
        font-size: 0.72rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase;
        color: #98a2b3; margin: 1rem 0 0.4rem;
      }
      #messageTemplatesModal .mt-group:first-child .mt-group-label { margin-top: 0; }
      #messageTemplatesModal .mt-card {
        border: 1px solid #e7e2da; border-radius: 12px; padding: 0.75rem 0.9rem; margin-bottom: 0.55rem;
        display: flex; gap: 0.9rem; align-items: flex-start;
      }
      #messageTemplatesModal .mt-card:hover { border-color: #c9c1b4; background: #fcfbf9; }
      #messageTemplatesModal .mt-card-body { flex: 1 1 auto; min-width: 0; }
      #messageTemplatesModal .mt-name { font-weight: 600; font-size: 0.92rem; }
      #messageTemplatesModal .mt-hint { font-size: 0.76rem; color: #6b7280; margin-top: 1px; }
      #messageTemplatesModal .mt-preview {
        white-space: pre-wrap; font-size: 0.8rem; line-height: 1.5; color: #374151;
        background: #f7f8fa; border-radius: 8px; padding: 0.55rem 0.7rem; margin-top: 0.5rem;
      }
      /* The bits the host still has to write, called out in the preview so the
         work left to do is obvious before they choose. */
      #messageTemplatesModal .mt-blank { background: #fff3cd; border-radius: 3px; padding: 0 2px; color: #7a5b0b; }
      #messageTemplatesModal .mt-token { color: #1a4fd6; }
      #messageTemplatesModal .mt-use { flex-shrink: 0; border-radius: 999px; }
      .mt-blanks-hint {
        display: none; margin-top: 0.35rem; font-size: 0.75rem; color: #856404;
        background: #fff8e1; border: 1px solid #ffe58a; border-radius: 6px; padding: 6px 10px;
      }
      .mt-open-btn {
        font-size: 0.75rem; background: #f3f0ea; color: #6b5b3e; border: 1px solid #ddd4c4;
        border-radius: 20px; padding: 2px 10px; cursor: pointer; font-weight: 500;
      }
      .mt-open-btn:hover { background: #ece7dd; }
    `;
    document.head.appendChild(st);
  }

  /** Preview markup: tokens tinted, [blanks] highlighted. */
  function previewHtml(body) {
    return esc(body)
      .replace(/\{\{(name|first_name)\}\}/g, (m) => `<span class="mt-token">${m}</span>`)
      .replace(/\[([^\]\n]{1,60})\]/g, (m) => `<span class="mt-blank">${m}</span>`);
  }

  function ensureModal() {
    let el = document.getElementById('messageTemplatesModal');
    if (el) return el;
    ensureStyles();
    el = document.createElement('div');
    el.id = 'messageTemplatesModal';
    el.className = 'modal fade';
    el.tabIndex = -1;
    el.innerHTML = `
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
        <div class="modal-content">
          <div class="modal-header py-2">
            <h6 class="modal-title"><i class="bi bi-stars me-2"></i><span class="mt-title">Message templates</span></h6>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted small mb-2 mt-lede"></p>
            <div class="mt-pick">
              <div class="mt-chips"></div>
              <div class="mt-list"></div>
            </div>
            <!-- The fill-in step (round 3): the blanks in the chosen template,
                 asked for once, with a live preview of the finished message. -->
            <div class="mt-fill" style="display:none">
              <div class="mt-fill-fields"></div>
              <div class="mt-fill-preview-label small fw-semibold mt-3 mb-1"></div>
              <div class="mt-preview mt-fill-preview"></div>
              <div class="d-flex justify-content-between align-items-center gap-2 mt-3">
                <button type="button" class="btn btn-link btn-sm px-0 mt-fill-back"></button>
                <button type="button" class="btn btn-primary btn-sm mt-fill-insert"></button>
              </div>
            </div>
          </div>
        </div>
      </div>`;
    document.body.appendChild(el);
    return el;
  }

  function closeModal(el) {
    const inst = window.bootstrap?.Modal?.getInstance(el);
    if (inst) {
      inst.hide();
      // Bootstrap ignores hide() while the SHOW transition is still running.
      // Pressing "Use this" the moment the picker appears is an ordinary thing
      // to do, and it would otherwise leave the dialog sitting open over a
      // message box it had already filled. hide() drops .show synchronously
      // when it takes, so if the class survives, it didn't — try again once the
      // dialog has finished arriving.
      if (el.classList.contains('show')) {
        el.addEventListener('shown.bs.modal', () => inst.hide(), { once: true });
      }
      return;
    }
    el.classList.remove('show');
    el.style.display = 'none';
    document.querySelectorAll('.modal-backdrop').forEach(b => b.remove());
    document.body.classList.remove('modal-open');
  }

  /**
   * Open the picker.
   *
   * @param {object} o
   * @param {'invitation'|'reminder'} o.kind
   * @param {string} [o.eventType]  the form's event type (invitation templates)
   * @param {string} [o.audience]   the reminder's audience (reminder templates)
   * @param {string} [o.lang]       the language being composed in
   * @param {string} [o.currentText] what is in the box now — a non-empty box is
   *        confirmed before being replaced, because a host who has written three
   *        paragraphs must not lose them to a mis-click.
   * @param {(body: string, template: object) => void} o.onPick
   */
  function open(o) {
    const opts = o || {};
    const el = ensureModal();
    const lang = langFor(opts.lang);
    const T = ui(lang);
    const groups = (opts.kind === 'reminder'
      ? reminders(opts.audience, lang)
      : invitations(opts.eventType, lang)
    ).filter(g => (g.templates || []).length);

    el.querySelector('.mt-title').textContent = opts.kind === 'reminder' ? T.reminderTitle : T.title;
    el.querySelector('.mt-lede').textContent = T.lede;
    // Hebrew and Arabic read right-to-left; the picker is nearly all prose.
    el.querySelector('.modal-content').setAttribute('dir', (lang === 'he' || lang === 'ar') ? 'rtl' : 'ltr');

    // Sections as chips rather than one long stack: the first group is the one
    // that fits this event (or this audience), so it leads and is selected.
    const chips = el.querySelector('.mt-chips');
    chips.innerHTML = [{ family: '__all', label: T.allSections }]
      .concat(groups.map(g => ({ family: g.family, label: g.label })))
      .map((c, i) => `<button type="button" class="mt-chip${i === 1 ? ' is-on' : ''}" data-family="${esc(c.family)}">${esc(c.label)}</button>`)
      .join('');
    // A single group needs no filter — one chip that filters to everything is
    // just furniture.
    chips.style.display = groups.length > 1 ? '' : 'none';

    el.querySelector('.mt-list').innerHTML = groups
      .map(g => `
        <div class="mt-group" data-family="${esc(g.family)}">
          <div class="mt-group-label">${esc(g.label)}</div>
          ${g.templates.map(t => `
            <div class="mt-card">
              <div class="mt-card-body">
                <div class="mt-name">${esc(t.name)}</div>
                <div class="mt-hint">${esc(t.hint)}</div>
                <div class="mt-preview">${previewHtml(t.body)}</div>
              </div>
              <button type="button" class="btn btn-primary btn-sm mt-use" data-template-id="${esc(t.id)}">${esc(T.use)}</button>
            </div>`).join('')}
        </div>`).join('');

    const showFamily = (family) => {
      el.querySelectorAll('.mt-group').forEach(g => {
        g.style.display = (family === '__all' || g.dataset.family === family) ? '' : 'none';
      });
      el.querySelectorAll('.mt-chip').forEach(c => c.classList.toggle('is-on', c.dataset.family === family));
    };
    chips.querySelectorAll('.mt-chip').forEach(c => {
      c.addEventListener('click', () => showFamily(c.dataset.family));
    });
    // Open on the section that fits — the reason the chips exist.
    showFamily(groups.length > 1 ? groups[0].family : '__all');

    const byId = {};
    groups.forEach(g => (g.templates || []).forEach(t => { byId[t.id] = t; }));

    const pickPane = el.querySelector('.mt-pick');
    const fillPane = el.querySelector('.mt-fill');
    const lede = el.querySelector('.mt-lede');
    const titleEl = el.querySelector('.mt-title');
    const pickTitle = titleEl.textContent;
    const pickLede = lede.textContent;

    const showPicker = () => {
      pickPane.style.display = '';
      fillPane.style.display = 'none';
      titleEl.textContent = pickTitle;
      lede.textContent = pickLede;
    };

    const deliver = (body, template) => {
      closeModal(el);
      try { opts.onPick && opts.onPick(body, template); }
      catch (e) { console.warn('Template insert failed:', e); }
    };

    /**
     * Ask for the template's blanks, then substitute them (round 3).
     *
     * The host asked for exactly this: "if I click a template it should have a
     * modal pop-up with the placeholders that I need to fill in and then
     * automatically populate it in the message". Each DISTINCT blank is asked
     * once and replaced everywhere — the formal wedding invitation carries
     * [names] twice, and asking twice is how a message ends up signed by
     * someone other than the couple it names.
     */
    const showFill = (t) => {
      const fields = uniqueBlanks(t.body);
      // Nothing to ask for: don't make the host click through an empty form.
      if (!fields.length) { deliver(t.body, t); return; }

      const values = {};
      // What the event already knows — a host should not have to retype their
      // own date and venue into a message we are composing for that event.
      const pre = opts.prefill || {};
      fields.forEach((b) => { if (pre[b]) values[b] = String(pre[b]); });

      titleEl.textContent = T.fillTitle;
      lede.textContent = T.fillLede;
      el.querySelector('.mt-fill-fields').innerHTML = fields.map((b, i) => `
        <div class="mt-fill-field">
          <label for="mt-fill-${i}">${esc(blankLabel(b))}</label>
          <input type="text" id="mt-fill-${i}" data-blank="${esc(b)}"
                 value="${esc(values[b] || '')}" autocomplete="off">
        </div>`).join('');
      el.querySelector('.mt-fill-preview-label').textContent = T.previewLabel;
      el.querySelector('.mt-fill-back').textContent = T.back;
      el.querySelector('.mt-fill-insert').textContent = T.insert;

      const preview = el.querySelector('.mt-fill-preview');
      const sync = () => { preview.innerHTML = previewHtml(fillBlanks(t.body, values)); };
      el.querySelectorAll('.mt-fill-field input').forEach((inp) => {
        inp.addEventListener('input', () => { values[inp.dataset.blank] = inp.value; sync(); });
      });
      sync();

      pickPane.style.display = 'none';
      fillPane.style.display = '';
      el.querySelector('.mt-fill-back').onclick = showPicker;
      el.querySelector('.mt-fill-insert').onclick = () => deliver(fillBlanks(t.body, values), t);
      // Enter anywhere in the form finishes it, the way a short form should.
      fillPane.onkeydown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); deliver(fillBlanks(t.body, values), t); }
      };
      setTimeout(() => el.querySelector('.mt-fill-field input')?.focus(), 60);
    };

    el.querySelectorAll('.mt-use').forEach(btn => {
      btn.addEventListener('click', () => {
        const t = byId[btn.dataset.templateId];
        if (!t) return;
        const existing = String(opts.currentText || '').trim();
        if (existing && existing !== String(opts.defaultText || '').trim()) {
          if (!window.confirm(T.replace)) return;
        }
        showFill(t);
      });
    });
    // Reopening the picker must not land on the previous pick's fill form.
    showPicker();

    if (window.bootstrap?.Modal) {
      window.bootstrap.Modal.getOrCreateInstance(el, { backdrop: true, keyboard: true }).show();
    } else {
      el.style.display = 'block';
      el.classList.add('show');
    }
    return el;
  }

  /** The button that opens the picker, for placing beside the message box. */
  function openButtonHtml(id, label) {
    ensureStyles();
    return `<button type="button" class="mt-open-btn" id="${esc(id)}"><i class="bi bi-stars me-1"></i>${esc(label || 'Templates')}</button>`;
  }

  /**
   * Warn while a message still carries [bracketed] blanks.
   *
   * This is the whole safety story for templates: a starting point is only
   * useful if the host cannot accidentally send it verbatim.
   */
  function wireBlanksHint(textarea, hint, preferredLang) {
    if (!textarea || !hint) return;
    const T = ui(langFor(preferredLang));
    const sync = () => {
      const found = blanks(textarea.value);
      if (!found.length) { hint.style.display = 'none'; return; }
      const shown = found.slice(0, 3).join(', ');
      const more = found.length > 3
        ? ' ' + String(T.andMore).replace('{n}', found.length - 3) + '.'
        : '.';
      hint.innerHTML = `<i class="bi bi-exclamation-triangle me-1"></i>`
        + `${esc(T.blanksLead)} <strong>${esc(shown)}</strong>${esc(more)}`;
      hint.style.display = 'block';
    };
    textarea.addEventListener('input', sync);
    sync();
    return sync;
  }

  return {
    FAMILY_BY_EVENT_TYPE,
    TEMPLATE_I18N,
    familyFor,
    langFor,
    ui,
    invitations,
    reminders,
    blanks,
    uniqueBlanks,
    knownBlanks,
    unfilledBlanks,
    prefillFromEvent,
    fillBlanks,
    blankLabel,
    previewHtml,
    open,
    openButtonHtml,
    wireBlanksHint
  };
})();
